import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Zap, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';
import QuoteForm from '../components/QuoteForm';

const ElectricalConnections = () => {
  const [openFaqIndex, setOpenFaqIndex] = React.useState(null);

  // Full 73 areas: UK + England + Wales + 48 English counties + 22 Welsh areas
  const areaServed = [
    { "@type": "Country", "name": "United Kingdom" },
    { "@type": "AdministrativeArea", "name": "England" },
    { "@type": "AdministrativeArea", "name": "Wales" },
    // English counties (48)
    { "@type": "AdministrativeArea", "name": "Bedfordshire" },
    { "@type": "AdministrativeArea", "name": "Berkshire" },
    { "@type": "AdministrativeArea", "name": "Bristol" },
    { "@type": "AdministrativeArea", "name": "Buckinghamshire" },
    { "@type": "AdministrativeArea", "name": "Cambridgeshire" },
    { "@type": "AdministrativeArea", "name": "Cheshire" },
    { "@type": "AdministrativeArea", "name": "City of London" },
    { "@type": "AdministrativeArea", "name": "Cornwall" },
    { "@type": "AdministrativeArea", "name": "Cumbria" },
    { "@type": "AdministrativeArea", "name": "Derbyshire" },
    { "@type": "AdministrativeArea", "name": "Devon" },
    { "@type": "AdministrativeArea", "name": "Dorset" },
    { "@type": "AdministrativeArea", "name": "County Durham" },
    { "@type": "AdministrativeArea", "name": "East Riding of Yorkshire" },
    { "@type": "AdministrativeArea", "name": "East Sussex" },
    { "@type": "AdministrativeArea", "name": "Essex" },
    { "@type": "AdministrativeArea", "name": "Gloucestershire" },
    { "@type": "AdministrativeArea", "name": "Greater London" },
    { "@type": "AdministrativeArea", "name": "Greater Manchester" },
    { "@type": "AdministrativeArea", "name": "Hampshire" },
    { "@type": "AdministrativeArea", "name": "Herefordshire" },
    { "@type": "AdministrativeArea", "name": "Hertfordshire" },
    { "@type": "AdministrativeArea", "name": "Isle of Wight" },
    { "@type": "AdministrativeArea", "name": "Kent" },
    { "@type": "AdministrativeArea", "name": "Lancashire" },
    { "@type": "AdministrativeArea", "name": "Leicestershire" },
    { "@type": "AdministrativeArea", "name": "Lincolnshire" },
    { "@type": "AdministrativeArea", "name": "Merseyside" },
    { "@type": "AdministrativeArea", "name": "Norfolk" },
    { "@type": "AdministrativeArea", "name": "North Yorkshire" },
    { "@type": "AdministrativeArea", "name": "Northamptonshire" },
    { "@type": "AdministrativeArea", "name": "Northumberland" },
    { "@type": "AdministrativeArea", "name": "Nottinghamshire" },
    { "@type": "AdministrativeArea", "name": "Oxfordshire" },
    { "@type": "AdministrativeArea", "name": "Rutland" },
    { "@type": "AdministrativeArea", "name": "Shropshire" },
    { "@type": "AdministrativeArea", "name": "Somerset" },
    { "@type": "AdministrativeArea", "name": "South Yorkshire" },
    { "@type": "AdministrativeArea", "name": "Staffordshire" },
    { "@type": "AdministrativeArea", "name": "Suffolk" },
    { "@type": "AdministrativeArea", "name": "Surrey" },
    { "@type": "AdministrativeArea", "name": "Tyne and Wear" },
    { "@type": "AdministrativeArea", "name": "Warwickshire" },
    { "@type": "AdministrativeArea", "name": "West Midlands" },
    { "@type": "AdministrativeArea", "name": "West Sussex" },
    { "@type": "AdministrativeArea", "name": "West Yorkshire" },
    { "@type": "AdministrativeArea", "name": "Wiltshire" },
    { "@type": "AdministrativeArea", "name": "Worcestershire" },
    // Welsh areas (22)
    { "@type": "AdministrativeArea", "name": "Blaenau Gwent" },
    { "@type": "AdministrativeArea", "name": "Bridgend" },
    { "@type": "AdministrativeArea", "name": "Caerphilly" },
    { "@type": "AdministrativeArea", "name": "Cardiff" },
    { "@type": "AdministrativeArea", "name": "Carmarthenshire" },
    { "@type": "AdministrativeArea", "name": "Ceredigion" },
    { "@type": "AdministrativeArea", "name": "Conwy" },
    { "@type": "AdministrativeArea", "name": "Denbighshire" },
    { "@type": "AdministrativeArea", "name": "Flintshire" },
    { "@type": "AdministrativeArea", "name": "Gwynedd" },
    { "@type": "AdministrativeArea", "name": "Isle of Anglesey" },
    { "@type": "AdministrativeArea", "name": "Merthyr Tydfil" },
    { "@type": "AdministrativeArea", "name": "Monmouthshire" },
    { "@type": "AdministrativeArea", "name": "Neath Port Talbot" },
    { "@type": "AdministrativeArea", "name": "Newport" },
    { "@type": "AdministrativeArea", "name": "Pembrokeshire" },
    { "@type": "AdministrativeArea", "name": "Powys" },
    { "@type": "AdministrativeArea", "name": "Rhondda Cynon Taf" },
    { "@type": "AdministrativeArea", "name": "Swansea" },
    { "@type": "AdministrativeArea", "name": "Torfaen" },
    { "@type": "AdministrativeArea", "name": "Vale of Glamorgan" },
    { "@type": "AdministrativeArea", "name": "Wrexham" }
  ];

  // County names for visible "Where we work" section
  const counties = [
    "Bedfordshire", "Berkshire", "Bristol", "Buckinghamshire", "Cambridgeshire", "Cheshire", "City of London", "Cornwall", "Cumbria", "Derbyshire", "Devon", "Dorset", "County Durham", "East Riding of Yorkshire", "East Sussex", "Essex", "Gloucestershire", "Greater London", "Greater Manchester", "Hampshire", "Herefordshire", "Hertfordshire", "Isle of Wight", "Kent", "Lancashire", "Leicestershire", "Lincolnshire", "Merseyside", "Norfolk", "North Yorkshire", "Northamptonshire", "Northumberland", "Nottinghamshire", "Oxfordshire", "Rutland", "Shropshire", "Somerset", "South Yorkshire", "Staffordshire", "Suffolk", "Surrey", "Tyne and Wear", "Warwickshire", "West Midlands", "West Sussex", "West Yorkshire", "Wiltshire", "Worcestershire",
    "Blaenau Gwent", "Bridgend", "Caerphilly", "Cardiff", "Carmarthenshire", "Ceredigion", "Conwy", "Denbighshire", "Flintshire", "Gwynedd", "Isle of Anglesey", "Merthyr Tydfil", "Monmouthshire", "Neath Port Talbot", "Newport", "Pembrokeshire", "Powys", "Rhondda Cynon Taf", "Swansea", "Torfaen", "Vale of Glamorgan", "Wrexham"
  ];

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://greenfrogutilities.co.uk/electrical-connections#service",
    "serviceType": "Electrical Connections",
    "name": "New Electrical Connections and ICP Services",
    "description": "NERS accredited independent connection provider delivering new electrical connections, three phase supplies, HV and LV connections, substations and supply upgrades across mainland UK.",
    "provider": { "@id": "https://greenfrogutilities.co.uk/#localbusiness" },
    "areaServed": areaServed,
    "hasCredential": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "certification",
      "name": "NERS accredited independent connection provider",
      "recognizedBy": {
        "@type": "Organization",
        "name": "National Electricity Registration Scheme"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Electrical Connection Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "New Electrical Connections" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Three Phase Supplies" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "HV and LV Connections" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Substation Design and Installation" }},
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Supply Upgrades and Diversions" }}
      ]
    }
  };

  const faqData = [
    {
      question: "What is a NERS accredited ICP?",
      answer: "An independent connection provider, or ICP, is a contractor accredited under the National Electricity Registration Scheme to design and build electrical connections that are adopted into the distribution network. Only a NERS accredited provider can carry out the contestable works. Green Frog Utilities is NERS accredited."
    },
    {
      question: "What is the difference between an ICP and the network operator?",
      answer: "The distribution network operator owns and runs the local network. An ICP carries out the contestable part of a connection, the design and construction, in competition with the DNO. Using an ICP generally gives more control over programme and cost, while the DNO still handles the non contestable elements and final energisation."
    },
    {
      question: "How do I get a new electrical connection for a development?",
      answer: "Provide the site address and the load required. The connection is then designed, a fixed quotation is issued, the non contestable elements are agreed with the network operator, and the installation is carried out and energised."
    },
    {
      question: "Do you install three phase supplies?",
      answer: "Yes. Three phase supplies are installed where the load is beyond what a single phase supply can carry, which is common on commercial and industrial sites."
    },
    {
      question: "Can electricity be installed alongside gas and water?",
      answer: "Yes. A multi utility connection installs all the required utilities under one design and one programme, so the site is not opened up several times over."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://greenfrogutilities.co.uk/" },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://greenfrogutilities.co.uk/services/connections" },
      { "@type": "ListItem", "position": 3, "name": "Electrical Connections", "item": "https://greenfrogutilities.co.uk/electrical-connections" }
    ]
  };

  const services = [
    "New electrical connections for developments, commercial units and industrial sites",
    "Three phase supplies where single phase is not enough for the load",
    "HV and LV connections, including new high voltage supplies",
    "Substations, from design through to installation",
    "Supply upgrades where an existing connection no longer meets demand",
    "Disconnections and diversions where an existing cable is in the way of construction",
    "Multi utility connections, with electricity installed alongside gas and water under one programme"
  ];

  const processSteps = [
    { step: "1", title: "Enquiry", desc: "Site address, the utilities required and the load needed, where known." },
    { step: "2", title: "Load assessment and design", desc: "The connection is designed around the load, the site layout and the existing distribution network." },
    { step: "3", title: "Quotation", desc: "A fixed quotation is issued against that design." },
    { step: "4", title: "Agreement with the network operator", desc: "The non contestable elements are agreed with the DNO." },
    { step: "5", title: "Installation", desc: "Cabling, ducting and any substation work are carried out by our teams." },
    { step: "6", title: "Energisation and handover", desc: "The supply is energised and the connection is adopted." }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div>
      <SEO 
        title="Electrical Connections | NERS Accredited ICP | Green Frog Utilities"
        description="NERS accredited independent connection provider. New electrical connections, HV and LV supplies, substations and three phase power for UK developments."
        canonical="/electrical-connections"
        jsonLd={[serviceSchema, faqSchema, breadcrumbSchema]}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <div className="flex justify-center mb-6">
              <Zap className="h-16 w-16" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              New Electrical Connections
            </h1>
            <p className="text-xl text-emerald-100 max-w-4xl mx-auto leading-relaxed">
              Green Frog Utilities is a NERS accredited independent connection provider, delivering new electrical connections for developments, commercial premises and industrial sites across mainland UK. Working as an ICP means the design and the contestable construction work are carried out by us rather than queued with the regional network operator, which is usually where connection programmes slip.
            </p>
          </div>
        </div>
      </section>

      {/* NERS Accredited Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              NERS accredited independent connection provider
            </h2>
            <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
              <p>
                NERS is the National Electricity Registration Scheme. It is the accreditation that allows a contractor to design and build electrical connections that will be adopted into the distribution network, and it is what separates an ICP from a general electrical contractor.
              </p>
              <p>
                Green Frog Utilities holds NERS accreditation. That means your connection is designed and installed by one contractor to your programme, with the distribution network operator involved for the non contestable elements and final energisation rather than for the whole job.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            Connections we deliver
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="border-none hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{service}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Who we work with
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Electrical connection enquiries come mainly from developers, main contractors, M and E consultants and site teams. What they need is a design they can rely on, a fixed price they can put in a cost plan, and an energisation date that holds.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How a new electrical connection works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((phase, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-emerald-300 mb-4">{phase.step}</div>
                <h3 className="text-xl font-bold mb-2">{phase.title}</h3>
                <p className="text-emerald-100">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              An independent connection provider in the Midlands and nationwide
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed">
              Green Frog Utilities works across mainland UK from its office at Unit 4 Ninian Park, Ninian Way, Tamworth, Staffordshire. The Midlands base covers Birmingham, the West Midlands and Staffordshire, and our teams work nationally from there.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div 
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  {openFaqIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  )}
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-6 bg-white">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Links Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/services/connections">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Gas Connections
              </Button>
            </Link>
            <Link to="/water-connections">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Water Connections
              </Button>
            </Link>
            <Link to="/metering">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Metering
              </Button>
            </Link>
            <Link to="/accreditations">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Accreditations
              </Button>
            </Link>
            <Link to="/why-choose-green-frog">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Why Choose Us
              </Button>
            </Link>
            <Link to="/green-frog-group">
              <Button variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                Utilities and Asset Management
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Where We Work Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Where we work</h2>
          <p className="text-gray-600 mb-6">
            Green Frog Utilities delivers gas, water and electrical connections across England and Wales, from a single accredited contractor.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
            {counties.map((county, index) => (
              <span key={index} className="text-sm text-gray-500">{county}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Get a quotation for your electrical connection
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Send the site address and the load required, and a design and quotation can be prepared.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#quote-form">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Get a Quote
                </Button>
              </a>
              <a href="tel:+441675488644">
                <Button size="lg" variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white">
                  Call 01675 488644
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <QuoteForm defaultConnectionType="electrical" defaultService="electrical-connections" title="Get an Electrical Connection Quote" />
    </div>
  );
};

export default ElectricalConnections;
